import type {SelectProps} from './new-stuff';

import * as React from 'react';

import {DefaultValueComponent, InputAndOptions, Placeholder} from './new-stuff';
import {useSenseTypeahead} from './hooks';

import classify from 'src/utils/classify';

import {ButtonTextSmall} from '@spaced-out/ui-design-system/lib/components/Text';

import {Icon} from '@spaced-out/ui-design-system/lib/components/Icon';

import css from './style.css';
import legacyCss from './legacy.css';
import genesisCss from './genesis.css';


function GenesisValueComponent<V>({
  value,
  resolveLabel,
}: {
  value: V;
  resolveLabel: (
    arg1: V,
  ) => string | React.ReactElement<React.ComponentProps<'div'>>;
}) {
  return (
    <div className={css.selectorValue}>
      <ButtonTextSmall>{resolveLabel(value)}</ButtonTextSmall>
    </div>
  );
}

/**
 * an input that allows selecting a value from a limited set of options.
 *
 * Very similar to the HTML `select` tag but with more bells and whistles.
 *
 * @param  props.value  the controlled value
 * @param  props.onChange  the handler that is fired when the user tries to change the value
 * @param  props.name  the name of the input (useful if it is part of a form)
 * @param  props.limit  the maximum number of item the user can add
 * @param  props.TokenComponent  a custom component used to render each token within the input
 *
 * @param  props.options  total list of allowed values for this field. will be used to provide suggestions.
 * @param  props.searchable  whether or not to filter the list of options when the user types in the input
 * @param  props.allowArbitraryValues  whether or not the user can specify their own custom values
 * @param  props.validateArbitraryValue  a function that determines if a custom value is valid
 * @param  props.formatArbitraryOptionLabel  a function that customizes the custom value when it shows up as a suggestion
 * @param  props.searchOnEmptyString  whether or not to show suggestions when there is no text in the search input
 * @param  props.groupOptionsBy  a function that produces a category key for any given option. if specified, suggestions will be grouped into sections by category.
 * @param  props.sortOptionsBy  a comparison function that determines the sort order of suggestions. by default it sorts them alphabetically.
 */
export function Select<V>(props: SelectProps<V>) {
  const handleAddOption = (option: V) => {
    onChange(option);
    inputProps.ref.current?.blur();
  };
  const {
    value,
    onChange,
    name,
    searchable,
    clearable,
    disabled,
    autofocus,
    placeholder,
    hasLabel,
    optionsDirection,
    baseClassNames = legacyCss,
    classNames,
    error,
    errorText,
    showAllOptions,
    components = {},
    // @ts-expect-error - TS2339 - Property 'value' does not exist on type 'V'.
    resolveKey = (option: V) => option.value,
    // @ts-expect-error - TS2339 - Property 'label' does not exist on type 'V'.
    resolveLabel = (option: V) => option.label,
  } = props;

  const {ValueComponent = GenesisValueComponent} = components;

  const [
    {selectedIndex},
    filteredOptions,
    groupedOptions,
    inputProps,
    handleContainerMouseDown,
    handleSelect,
    {isSearching, focused, isShowingOptions},
  ] = useSenseTypeahead({
    onAddOption: handleAddOption,
    ...props,
    resolveKey,
    resolveLabel,
  });

  const showClearButton = clearable && value && !disabled;
  const showChevron = !focused && !showClearButton && !disabled;

  // returns true when a value exists or can be determined using
  // resolveKey (which is used to fetch V typed values)
  const hasValue = (data?: V | null): boolean => {
    if (data == null) {
      return false;
    }

    if (resolveKey(data) == null) {
      return false;
    }

    return true;
  };

  return (
    <InputAndOptions
      error={error}
      errorText={errorText}
      baseClassNames={baseClassNames}
      classNames={classNames}
      options={props.groupOptionsBy ? groupedOptions : filteredOptions}
      optionsDirection={optionsDirection}
      showAllOptions={showAllOptions}
      selectedIndex={searchable ? selectedIndex : null}
      onSelect={handleSelect}
      onContainerMouseDown={handleContainerMouseDown}
      components={components}
      inputProps={{
        ...props.inputProps,
        // @ts-expect-error - TS2353 - Object literal may only specify known properties, and 'outerRef' does not exist in type '{ disabled?: unknown; } & ClassAttributes<HTMLInputElement> & InputHTMLAttributes<HTMLInputElement>'.
        outerRef: props.inputProps?.ref,
        disabled,
        ...inputProps,
      }}
      isSearching={isSearching}
      isFocused={focused}
      isShowingOptions={isShowingOptions}
      resolveLabel={resolveLabel}
      resolveKey={resolveKey}
      suffixLabel={
        showClearButton ? (
          <div className={css.clearButton} onMouseDown={() => onChange(null)}>
            <Icon name="xmark" />
          </div>
        ) : (
          showChevron && (
            <div>
              <Icon
                name="sort-down"
                type="solid"
                className={classify(
                  css.chevron,
                  genesisCss.chevron,
                  optionsDirection && css[optionsDirection],
                )}
              />
            </div>
          )
        )
      }
      autofocus={autofocus}
      // @ts-expect-error - TS17001 - JSX elements cannot have multiple attributes with the same name.
      resolveLabel={resolveLabel}
      resolveKey={resolveKey}
    >
      {!isSearching && value != null && (
        <ValueComponent value={value} resolveLabel={resolveLabel} />
      )}
      {placeholder && (
        <Placeholder
          className={baseClassNames.placeholder}
          placeholder={placeholder}
          isLabel={hasLabel}
          isHidden={isSearching || hasValue(value)}
        />
      )}
      <input
        type="hidden"
        name={name}
        value={value != null ? resolveKey(value) : null}
      />
    </InputAndOptions>
  );
}
